import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import dayjs from 'dayjs';
import {
    AlertTriangle,
    ArrowUpRight,
    BarChart3,
    Clock,
    CreditCard,
    DollarSign,
    Gem,
    Gift,
    PieChart,
    RefreshCw,
    Repeat,
    RotateCw,
    UserCheck,
    UserMinus,
    UserPlus,
    Users,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { httpClient } from '../../common/httpclient';
import { useAppBarAction, useAppBarSubtitle } from '../../component/layout/AppBarActions';
import { KitCard, SecH } from '../../component/common/MobileKit';
import { HBars, Kpi, KpiGrid, KitEmptyNote, ProgressBar, formatMoneyShort } from '../../component/common/MobileDashboardKit';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import { usePermissions } from '../../hooks/usePermissions';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import DesktopPeriodSelector from '../../component/common/DesktopPeriodSelector';
import KpiTickerCard, { KpiDeltaChip } from '../../component/report/KpiTickerCard';
import AnimatedNumber from '../../component/common/AnimatedNumber';
import { IndicatorHelp, IndicatorGlossaryButton } from '../../component/common/IndicatorHelp';
import { DashboardBoard, DashboardEditBar, DashboardLayoutChip, DashboardPersonalizeButton, Indicator, useDashboardLayout } from '../../component/common/DashboardCustomize';
import { CUSTOMER_INDICATOR_HELP as HELP, CUSTOMER_GLOSSARY_ORDER } from './customerIndicatorHelp';
import './dashboard.css';

const GLOSSARY_ITEMS = CUSTOMER_GLOSSARY_ORDER.map((k) => HELP[k]);

// Indicadores personalizáveis (desktop), na ordem de exibição. Ids são persistidos no backend: não renomear.
// w = largura padrão em colunas da grade de 12; minW = largura mínima (a altura mínima é a do conteúdo).
const LAYOUT_REGISTRY = [
    { id: 'cadastrados', label: 'Clientes cadastrados', w: 6, minW: 4 },
    { id: 'novos', label: 'Novos clientes', w: 3, minW: 3 },
    { id: 'ticket', label: 'Ticket médio por cliente', w: 3, minW: 3 },
    { id: 'valor-gerado', label: 'Valor total gerado', w: 3, minW: 3 },
    { id: 'frequencia', label: 'Frequência média de retorno', w: 3, minW: 3 },
    { id: 'perfil', label: 'Perfil de clientes (PF / PJ)', w: 3, minW: 3 },
    { id: 'aniversariantes', label: 'Aniversariantes no período', w: 3, minW: 3 },
    { id: 'incompleto', label: 'Cadastro incompleto', w: 3, minW: 3 },
    { id: 'ltv', label: 'LTV médio por cliente', w: 3, minW: 3 },
    { id: 'recorrencia', label: 'Taxa de recorrência', w: 3, minW: 3 },
    { id: 'em-risco', label: 'Em risco de inatividade', w: 3, minW: 3 },
    { id: 'reativados', label: 'Clientes reativados', w: 3, minW: 3 },
    { id: 'concentracao', label: 'Concentração (top 10)', w: 3, minW: 3 },
    { id: 'grafico-crescimento', label: 'Gráfico: Crescimento da base de clientes', w: 6, minW: 4 },
    { id: 'grafico-fluxo', label: 'Gráfico: Novos x Inativados x Reativados', w: 6, minW: 4 },
    { id: 'faixas-gasto', label: 'Clientes por faixa de gasto', w: 4, minW: 4 },
    { id: 'maior-valor', label: 'Maior valor gasto', w: 4, minW: 4 },
    { id: 'maior-frequencia', label: 'Maior frequência', w: 4, minW: 4 },
    { id: 'lista-aniversariantes', label: 'Lista: aniversariantes do período', w: 6, minW: 4 },
    { id: 'lista-inativos', label: 'Lista: clientes inativos', w: 6, minW: 4 },
];

/**
 * Dashboard de Clientes (aba "Clientes" do hub /dashboards/customer).
 * Ref: `ui-ux/prototipo-dashboard-clientes.html`. Todos os números vêm de
 * `GET /dashboard/customer` (fv-api/dashboard/customer.go) — nada é calculado
 * ou estimado aqui além de formatação e razões de exibição (ex.: % da base).
 *
 * Regras do backend (rótulos fiéis ao cálculo):
 * - compra = venda "Finalizada" ou OS "Paga" de cliente ativo identificado;
 * - inativo = última compra (ou cadastro, se nunca comprou) há mais de 60 dias
 *   na data de corte (fim do período, limitado a hoje); "inativos +90 dias" idem;
 * - em risco = última compra entre 30 e 60 dias atrás;
 * - reativado = inativo que voltou a comprar dentro do período;
 * - variação vs. período anterior: o backend devolve o período anterior
 *   (mesma duração) no mesmo payload (`previous`).
 */

const REPORT_PATH = '/reports/customers';

export const fmtInt = (n) => Math.round(n || 0).toLocaleString('pt-BR');
const fmtBrl = (cents) => `R$ ${((cents || 0) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtBrl0 = (cents) => `R$ ${Math.round((cents || 0) / 100).toLocaleString('pt-BR')}`;
export const fmtPct1 = (v) => `${(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
export const pctOf = (part, total) => (total > 0 ? (part / total) * 100 : 0);
export const isoToBr = (iso) => (iso ? iso.split('-').reverse().join('/') : '');
export const isoToDm = (iso) => (iso ? iso.split('-').reverse().slice(0, 2).join('/') : '');
export const MONTHS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const isoToMonthLabel = (iso) => {
    const [y, m] = iso.split('-');
    return `${MONTHS[Number(m) - 1]}/${y.slice(2)}`;
};
const initials = (name) => String(name || '')
    .replace(/\b(Ltda|ME|EPP|SA|de|da|do|dos)\b/gi, '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

const bandLabel = (band) => {
    const from = Math.round(band.from_cents / 100).toLocaleString('pt-BR');
    if (band.to_cents == null) return `R$ ${from}+`;
    return `R$ ${from} – ${Math.round(band.to_cents / 100).toLocaleString('pt-BR')}`;
};

// Card header (`.card-head`): ícone 36px + título + subtítulo (+ total à direita) — DESIGN-SYSTEM.md §5.2.
export function CardHeadRow({ icon, iconBg, iconFg, title, subtitle, loading, right, help }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: '10px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: iconBg, color: iconFg }}>
                {icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: 'text.primary', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {title}
                    {help && <IndicatorHelp help={help} />}
                </Typography>
                <Typography sx={{ fontSize: '11.5px', color: 'text.disabled', mt: '1px' }}>
                    {loading ? <Skeleton variant="text" width={140} /> : subtitle}
                </Typography>
            </Box>
            {right && <Box sx={{ ml: 'auto', textAlign: 'right', lineHeight: 1.1 }}>{right}</Box>}
        </Box>
    );
}

// Link "Ver no relatório" -> /reports/customers (relatório completo, sem abas/tabelas aqui).
function ReportLink() {
    return (
        <Box
            component={RouterLink}
            to={REPORT_PATH}
            title="Abrir o Relatório de Clientes"
            sx={{ ml: 'auto', display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '11.5px', fontWeight: 700, color: 'primary.main', textDecoration: 'none', whiteSpace: 'nowrap', '&:hover': { textDecoration: 'underline' } }}
        >
            Ver no relatório <ArrowUpRight size={13} strokeWidth={2} />
        </Box>
    );
}

// `.summary-row` — rótulo (ícone + texto) à esquerda, valor em negrito à direita.
export function SummaryRow({ icon, iconBg, iconFg, label, value, chip, loading }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1, gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <Box sx={{ width: 26, height: 26, borderRadius: '8px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: iconBg, color: iconFg }}>
                    {icon}
                </Box>
                <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>{label}</Typography>
            </Box>
            {loading ? (
                <Skeleton variant="text" width={70} height={20} />
            ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {chip}
                    <Typography sx={{ fontSize: '15px', fontWeight: 800, color: 'text.primary' }}>{value}</Typography>
                </Box>
            )}
        </Box>
    );
}

// Linha com trilha proporcional (faixas de gasto / rankings) — mesmo formato do PaymentRow do SaleDashboard.
export function BarRow({ lead, label, badge, barWidth, barColor, value, sub, loading }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: '10px', '& + &': { borderTop: '1px dashed', borderColor: 'divider' } }}>
            {lead}
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                    <Typography noWrap sx={{ fontSize: '13px', fontWeight: 600, color: 'text.secondary' }}>{label}</Typography>
                    {badge}
                </Box>
                <Box sx={{ height: 5, borderRadius: '4px', bgcolor: 'background.subtle', overflow: 'hidden' }}>
                    {!loading && <Box sx={{ height: '100%', borderRadius: '4px', width: `${barWidth}%`, background: barColor, transition: 'width .4s ease' }} />}
                </Box>
            </Box>
            <Box sx={{ textAlign: 'right', display: 'flex', flexDirection: 'column', gap: '1px', flexShrink: 0 }}>
                <Typography sx={{ fontSize: '14px', fontWeight: 800, color: 'text.primary', fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
                <Typography sx={{ fontSize: '10.5px', fontWeight: 600, color: 'text.disabled' }}>{sub}</Typography>
            </Box>
        </Box>
    );
}

const TypePill = ({ type }) => (
    <Box component="span" sx={{ fontSize: '9.5px', fontWeight: 800, px: '6px', py: '1px', borderRadius: '20px', flexShrink: 0, bgcolor: type === 'pj' ? 'secondary.bg' : 'info.bg', color: type === 'pj' ? 'secondary.main' : 'primary.main' }}>
        {type === 'pj' ? 'PJ' : 'PF'}
    </Box>
);

export const RankNum = ({ n }) => (
    <Box sx={{ width: 24, height: 24, borderRadius: '8px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11.5px', fontWeight: 800, bgcolor: 'background.subtle', color: 'text.secondary' }}>{n}</Box>
);

const Avatar = ({ name, bg, fg }) => (
    <Box sx={{ width: 34, height: 34, borderRadius: '10px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 800, bgcolor: bg, color: fg }}>
        {initials(name)}
    </Box>
);

// Linha de lista compacta (aniversariantes / inativos).
export function ListRow({ avatar, title, sub, end }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: '10px', '& + &': { borderTop: '1px dashed', borderColor: 'divider' } }}>
            {avatar}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography noWrap sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary' }}>{title}</Typography>
                <Typography noWrap component="div" sx={{ fontSize: '11.5px', color: 'text.disabled', mt: '1px', display: 'flex', alignItems: 'center', gap: '6px' }}>{sub}</Typography>
            </Box>
            <Box sx={{ textAlign: 'right', flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>{end}</Box>
        </Box>
    );
}

export const Pill = ({ tone, children }) => (
    <Box component="span" sx={{ fontSize: '10.5px', fontWeight: 700, px: '8px', py: '1px', borderRadius: '20px', bgcolor: `${tone}.bg`, color: `${tone}.main`, whiteSpace: 'nowrap' }}>{children}</Box>
);

// DESIGN-SYSTEM.md §5.11 empty state.
export function EmptyMiniState({ icon, title, subtitle }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 3, textAlign: 'center' }}>
            <Box sx={{ width: 56, height: 56, borderRadius: '50%', border: '2px dashed', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled' }}>{icon}</Box>
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'text.secondary' }}>{title}</Typography>
            <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>{subtitle}</Typography>
        </Box>
    );
}

export const ChartLegendItem = ({ color, label }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <Box sx={{ width: 9, height: 9, borderRadius: '3px', bgcolor: color, flexShrink: 0 }} />
        <Typography component="span" sx={{ fontSize: '11.5px', color: 'text.secondary' }}>{label}</Typography>
    </Box>
);

// Barra segmentada + legenda (PF/PJ, cadastro incompleto, em risco, concentração).
export function SegFooter({ parts, legend }) {
    return (
        <>
            <Box sx={{ display: 'flex', height: 8, borderRadius: '6px', overflow: 'hidden', gap: '2px', bgcolor: 'background.subtle', mb: '8px' }}>
                {parts.map((p) => <Box key={p.key} sx={{ width: `${Math.max(0, p.value)}%`, bgcolor: p.color }} />)}
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', fontSize: '10.5px', color: 'text.disabled', '& b': { color: 'text.secondary', fontWeight: 700 } }}>
                {legend.map((l) => (
                    <Box key={l.key} sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: l.color }} />
                        <span>{l.text}</span>
                    </Box>
                ))}
            </Box>
        </>
    );
}

export const GroupTitle = ({ children }) => (
    <Typography component="h3" sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'text.disabled', mt: 2, mb: '8px' }}>{children}</Typography>
);

export const tickerGridSx = {
    display: 'grid',
    gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
    gap: '10px',
    mb: 2,
    '& > *': { gridColumn: { xs: 'span 12', sm: 'span 6', lg: 'span 3' }, minWidth: 0 },
};

export default function CustomerDashboard() {
    const theme = useTheme();
    const isMobileShell = useIsMobileShell();
    const { hasPermission } = usePermissions();
    const canView = hasPermission('dashboard.customer');
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [failed, setFailed] = useState(false);
    // Mesmo padrão dos irmãos: período padrão = mês inteiro (o backend limita o corte a hoje).
    const [startDate, setStartDate] = useState(dayjs().startOf('month'));
    const [endDate, setEndDate] = useState(dayjs().endOf('month'));
    const requestId = useRef(0);
    const layout = useDashboardLayout('customer', LAYOUT_REGISTRY, canView && !isMobileShell);

    const loadData = useCallback(async () => {
        if (!canView) {
            setLoading(false);
            return;
        }
        if (!startDate?.isValid?.() || !endDate?.isValid?.()) return;
        const id = ++requestId.current;
        setLoading(true);
        setFailed(false);
        try {
            const response = await httpClient.get(`/dashboard/customer?startDate=${startDate.format('DD/MM/YYYY')}&endDate=${endDate.format('DD/MM/YYYY')}`);
            if (id !== requestId.current) return;
            if (response.status >= 200 && response.status < 300 && response.data) {
                setData(response.data);
            } else {
                setData(null);
                setFailed(true);
            }
        } catch (error) {
            console.error('Erro ao carregar o dashboard de clientes:', error);
            if (id === requestId.current) {
                setData(null);
                setFailed(true);
            }
        } finally {
            if (id === requestId.current) setLoading(false);
        }
    }, [startDate, endDate, canView]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    useAppBarAction({
        label: 'Atualizar',
        icon: RefreshCw,
        onClick: loadData,
        disabled: loading,
        spinning: loading,
        enabled: isMobileShell,
    });
    useAppBarSubtitle(isMobileShell ? 'Dashboard' : null);

    const cur = data?.current;
    const prev = data?.previous;
    const series = data?.series;
    const dates = series?.dates;
    const hasSeries = Boolean(dates && dates.length > 1);
    const trend = (pick, seriesKey, scale = 1) => {
        if (!cur || !prev) return undefined;
        const a = pick(cur);
        const b = pick(prev);
        if (a == null || b == null) return undefined;
        return {
            total: a * scale,
            previous_total: b * scale,
            series: hasSeries && seriesKey ? series[seriesKey].map((v) => v * scale) : undefined,
        };
    };
    const prevDays = data?.days || 0;

    const total = cur?.registered || 0;
    const inactive = cur?.inactive || 0;
    const inactiveShare = pctOf(inactive, total);
    const asOf = data?.as_of ? isoToBr(data.as_of) : '';

    // ------------------------------------------------------------------ mobile
    if (isMobileShell) {
        const bands = data?.spend_bands || [];
        const bandItems = bands.map((b) => ({ name: bandLabel(b), value: b.customers, text: fmtInt(b.customers) }));
        const topValue = (data?.top_by_value || []).map((c) => ({ name: c.name, value: c.value_cents, text: fmtBrl0(c.value_cents) }));
        const topFreq = (data?.top_by_frequency || []).map((c) => ({ name: c.name, value: c.interactions, text: fmtInt(c.interactions) }));
        return (
            <PageScaffold title="Dashboard de Clientes" titlePageTarget="Clientes" links={[{ title: 'Dashboard', link: '' }]}>
                {failed ? (
                    <KitCard><KitEmptyNote>Não foi possível carregar os indicadores de clientes</KitEmptyNote></KitCard>
                ) : (
                    <>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                            <IndicatorGlossaryButton items={GLOSSARY_ITEMS} fullScreen />
                        </Box>
                        <KpiGrid first>
                            <Kpi grad label="Clientes cadastrados" icon="users" value={fmtInt(total)} loading={loading} delta={{ text: `${fmtInt(cur?.new)} novos no período`, tone: 'up' }} />
                            <Kpi label="Inativos" icon="alert" value={fmtInt(inactive)} loading={loading} delta={{ text: `${fmtPct1(inactiveShare)} da base`, tone: 'warn' }} />
                            <Kpi label="Ticket médio" icon="wallet" value={fmtBrl(cur?.ticket_cents)} loading={loading} />
                            <Kpi label="Valor gerado" icon="dollar" value={formatMoneyShort((cur?.revenue_cents || 0) / 100)} loading={loading} />
                        </KpiGrid>
                        <SecH title="Retenção" />
                        <KitCard>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                                <b>{fmtPct1(cur?.recurrence_rate)}</b>
                                <Box component="span" sx={{ fontSize: 12.5, color: 'text.disabled' }}>{fmtInt(cur?.recurring)} de {fmtInt(cur?.buyers)} clientes compraram 2+ vezes</Box>
                            </Box>
                            <Box sx={{ mt: '8px' }}><ProgressBar percent={cur?.recurrence_rate || 0} /></Box>
                            <Box sx={{ mt: '10px', fontSize: 12.5, color: 'text.disabled' }}>
                                {fmtInt(cur?.at_risk)} em risco (30–60 dias) · {fmtInt(cur?.reactivated)} reativados
                            </Box>
                        </KitCard>
                        <SecH title="Clientes por faixa de gasto" />
                        <KitCard>{bandItems.some((b) => b.value > 0) ? <HBars items={bandItems} /> : <KitEmptyNote>Sem compras no período</KitEmptyNote>}</KitCard>
                        <SecH title="Maior valor gasto" />
                        <KitCard>{topValue.length ? <HBars items={topValue} valueWidth={80} /> : <KitEmptyNote>Sem compras no período</KitEmptyNote>}</KitCard>
                        <SecH title="Maior frequência" />
                        <KitCard>{topFreq.length ? <HBars items={topFreq} /> : <KitEmptyNote>Sem compras no período</KitEmptyNote>}</KitCard>
                    </>
                )}
            </PageScaffold>
        );
    }

    // ----------------------------------------------------------------- desktop
    const growthPoints = data?.growth?.points || [];
    const growthUnit = data?.growth?.unit;
    const pointLabel = (p) => (growthUnit === 'month' ? isoToMonthLabel(p.start) : isoToDm(p.start));
    const unitLabel = { day: 'dia', week: 'semana', month: 'mês' }[growthUnit] || 'dia';
    const hasGrowth = growthPoints.length > 0;
    const hasFlow = growthPoints.some((p) => p.new > 0 || p.inactivated > 0 || p.reactivated > 0);

    const bands = data?.spend_bands || [];
    const bandColors = [theme.palette.error.main, theme.palette.warning.main, theme.palette.primary.main, theme.palette.success.main];
    const bandMax = Math.max(...bands.map((b) => b.customers), 1);
    const buyers = cur?.buyers || 0;
    const topValue = data?.top_by_value || [];
    const topFreq = data?.top_by_frequency || [];
    const gradient = `linear-gradient(90deg, ${theme.palette.brand2.main}, ${theme.palette.primary.main})`;
    const bd = data?.birthdays;
    const inact = data?.inactive;

    const pfRound = cur ? Math.round(cur.pf_rate || 0) : 0;
    const incTotal = cur?.incomplete || 0;
    const baseOf = (n) => pctOf(n, total);

    const avgReturn = trend((k) => k.avg_return_days, 'avg_return_days');

    return (
        <PageScaffold title="Dashboard de Clientes" titlePageTarget="Clientes" links={[{ title: 'Dashboard', link: '' }]}>
            <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                        <Box sx={{ width: 38, height: 38, borderRadius: '11px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', background: (t) => t.palette.gradient.brand }}>
                            <Users size={18} strokeWidth={2} />
                        </Box>
                        <Box>
                            <Typography component="h1" data-page-title sx={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.3 }}>
                                Dashboard de Clientes
                                <DashboardLayoutChip layout={layout} />
                            </Typography>
                            <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', mt: 0.5 }}>
                                Crescimento, retenção e risco de inatividade da base, perfil e valor gerado por cliente, vs. período anterior.
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        {!failed && <DashboardPersonalizeButton layout={layout} />}
                        <DesktopPeriodSelector
                            start={startDate}
                            end={endDate}
                            onChange={(s, e) => { setStartDate(s); setEndDate(e); }}
                            onRefresh={loadData}
                            loading={loading}
                            dualMonth
                        />
                    </Box>
                </Box>

                {failed && !loading && (
                    <EmptyMiniState
                        icon={<Users size={22} strokeWidth={2} />}
                        title="Não foi possível carregar os indicadores"
                        subtitle="Tente atualizar o período em instantes"
                    />
                )}

                {!failed && (
                    <>
                        <DashboardEditBar layout={layout} />
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap', mb: '10px', mt: '4px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                {data && prevDays > 0 && (
                                    <Typography sx={{ fontSize: '10.5px', fontWeight: 600, color: 'text.disabled', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <Box component="i" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: 'success.main', display: 'inline-block' }} />
                                        {prevDays} {prevDays === 1 ? 'dia' : 'dias'} · vs. {prevDays} {prevDays === 1 ? 'dia anterior' : 'dias anteriores'} · posição em {asOf}
                                    </Typography>
                                )}
                                <IndicatorGlossaryButton items={GLOSSARY_ITEMS} />
                            </Box>
                            <Typography sx={{ fontSize: '11px', color: 'text.disabled', fontWeight: 500 }}>Variações comparam com o período anterior de mesmo tamanho</Typography>
                        </Box>

                        <DashboardBoard layout={layout}>
                            {/* Grupo A — base e aquisição. */}
                            <Indicator id="cadastrados">
                            <KpiTickerCard
                                hero large uid="cbase" kind="units"
                                label="Clientes cadastrados" help={HELP.base}
                                icon={<Users size={13} strokeWidth={2} />} iconBg="info.bg" iconFg="primary.main"
                                data={trend((k) => k.registered, 'base')} days={dates}
                                value={total} loading={loading}
                                footer={
                                    <>
                                        <Box title="Ativos x inativos (60 dias sem venda finalizada ou OS paga)" sx={{ display: 'flex', height: 8, borderRadius: '6px', overflow: 'hidden', gap: '2px', bgcolor: 'background.subtle', mb: '6px' }}>
                                            <Box sx={{ width: `${100 - inactiveShare}%`, bgcolor: 'success.main' }} />
                                            <Box sx={{ width: `${inactiveShare}%`, bgcolor: 'error.main' }} />
                                        </Box>
                                        <SummaryRow
                                            icon={<UserCheck size={13} strokeWidth={2} />} iconBg="success.bg" iconFg="success.main"
                                            label={<>Clientes ativos <Box component="small" sx={{ fontSize: '10.5px', color: 'text.disabled', fontWeight: 500, ml: '2px' }}>{fmtPct1(100 - inactiveShare)} da base</Box></>}
                                            value={<AnimatedNumber value={cur?.active || 0} />}
                                            loading={loading}
                                        />
                                        <SummaryRow
                                            icon={<UserMinus size={13} strokeWidth={2} />} iconBg="error.bg" iconFg="error.main"
                                            label={<>Clientes inativos <Box component="small" sx={{ fontSize: '10.5px', color: 'text.disabled', fontWeight: 500, ml: '2px' }}>{fmtPct1(inactiveShare)} · 60 dias sem venda/OS</Box></>}
                                            chip={<KpiDeltaChip kind="units" inverse data={cur && prev ? { total: cur.inactive, previous_total: prev.inactive } : undefined} />}
                                            value={<AnimatedNumber value={inactive} />}
                                            loading={loading}
                                        />
                                        <Typography sx={{ fontSize: '10.5px', color: 'text.disabled', mt: '4px' }}>
                                            Ativos = cadastrados − inativos · <b>{fmtInt(cur?.inactive_over_90)}</b> inativos há mais de 90 dias
                                        </Typography>
                                    </>
                                }
                            />
                            </Indicator>
                            <Indicator id="novos">
                            <KpiTickerCard uid="cnew" kind="units" label="Novos clientes" help={HELP.new} icon={<UserPlus size={11} strokeWidth={2} />} iconBg="success.bg" iconFg="success.main"
                                data={trend((k) => k.new, 'new')} days={dates} value={cur?.new || 0} loading={loading}
                                note={<><b>{fmtPct1(baseOf(cur?.new || 0))}</b> da base · cadastrados no período</>} />
                            </Indicator>
                            <Indicator id="ticket">
                            <KpiTickerCard uid="ctick" kind="money" label="Ticket médio por cliente" help={HELP.ticket} icon={<BarChart3 size={11} strokeWidth={2} />} iconBg="info.bg" iconFg="brand2.main"
                                data={trend((k) => k.ticket_cents, 'ticket_cents')} days={dates} value={cur?.ticket_cents || 0} loading={loading}
                                note={<>Receita ÷ <b>{fmtInt(buyers)}</b> clientes com compra</>} />
                            </Indicator>
                            <Indicator id="valor-gerado">
                            <KpiTickerCard uid="crev" kind="money" label="Valor total gerado" help={HELP.revenue} icon={<DollarSign size={11} strokeWidth={2} />} iconBg="success.bg" iconFg="success.main"
                                data={trend((k) => k.revenue_cents, 'revenue_cents')} days={dates} value={cur?.revenue_cents || 0} loading={loading}
                                note={<>Vendas + OS · <b>{fmtInt(cur?.interactions)}</b> interações</>} />
                            </Indicator>
                            <Indicator id="frequencia">
                            <KpiTickerCard inverse uid="cfreq" kind="days" label="Frequência média de retorno" help={HELP.frequency} icon={<Clock size={11} strokeWidth={2} />} iconBg="info.bg" iconFg="primary.main"
                                data={avgReturn} days={dates} value={cur?.avg_return_days ?? null} loading={loading}
                                note={avgReturn ? <>Intervalo médio entre compras · menor é melhor</> : undefined} />
                            </Indicator>

                            {/* Grupo B — perfil e qualidade da base. */}
                            <Indicator id="perfil">
                            <KpiTickerCard neutral hideSpark uid="cpf" kind="pct" label="Perfil de clientes (PF / PJ)" help={HELP.profile} icon={<Users size={11} strokeWidth={2} />} iconBg="secondary.bg" iconFg="secondary.main"
                                data={trend((k) => k.pf_rate, null, 100)} value={Math.round((cur?.pf_rate || 0) * 100)} valueText={cur && total > 0 ? `${pfRound}% / ${100 - pfRound}%` : undefined} loading={loading}
                                footer={cur && (
                                    <SegFooter
                                        parts={[{ key: 'pf', value: cur.pf_rate, color: theme.palette.primary.main }, { key: 'pj', value: total > 0 ? 100 - cur.pf_rate : 0, color: theme.palette.secondary.main }]}
                                        legend={[{ key: 'pf', color: theme.palette.primary.main, text: <><b>{fmtInt(cur.pf)}</b> Pessoa Física</> }, { key: 'pj', color: theme.palette.secondary.main, text: <><b>{fmtInt(cur.pj)}</b> Pessoa Jurídica</> }]}
                                    />
                                )} />
                            </Indicator>
                            <Indicator id="aniversariantes">
                            <KpiTickerCard neutral uid="cbd" kind="units" label="Aniversariantes no período" help={HELP.birthdays} icon={<Gift size={11} strokeWidth={2} />} iconBg="warning.bg" iconFg="warning.main"
                                data={trend((k) => k.birthdays, 'birthdays')} days={dates} value={cur?.birthdays || 0} loading={loading}
                                note={<>Oportunidade de contato e mensagem de parabéns</>} />
                            </Indicator>
                            <Indicator id="incompleto">
                            <KpiTickerCard inverse hideSpark uid="cinc" kind="units" label="Cadastro incompleto" help={HELP.incomplete} icon={<CreditCard size={11} strokeWidth={2} />} iconBg="error.bg" iconFg="error.main"
                                data={trend((k) => k.incomplete, null)} value={incTotal} loading={loading}
                                footer={cur && (
                                    <>
                                        <SegFooter
                                            parts={[
                                                { key: 'tel', value: baseOf(cur.incomplete_no_phone), color: theme.palette.warning.main },
                                                { key: 'mail', value: baseOf(cur.incomplete_no_email), color: theme.palette.secondary.main },
                                                { key: 'both', value: baseOf(cur.incomplete_both), color: theme.palette.error.main },
                                            ]}
                                            legend={[
                                                { key: 'tel', color: theme.palette.warning.main, text: <><b>{fmtInt(cur.incomplete_no_phone)}</b> sem telefone</> },
                                                { key: 'mail', color: theme.palette.secondary.main, text: <><b>{fmtInt(cur.incomplete_no_email)}</b> sem e-mail</> },
                                                { key: 'both', color: theme.palette.error.main, text: <><b>{fmtInt(cur.incomplete_both)}</b> ambos</> },
                                            ]}
                                        />
                                        <Typography sx={{ fontSize: '10.5px', color: 'text.disabled', mt: '6px' }}><b>{fmtPct1(baseOf(incTotal))}</b> da base · limita campanhas</Typography>
                                    </>
                                )} />
                            </Indicator>
                            <Indicator id="ltv">
                            <KpiTickerCard uid="cltv" kind="money" label="LTV médio por cliente" help={HELP.ltv} icon={<Gem size={11} strokeWidth={2} />} iconBg="success.bg" iconFg="success.main"
                                data={trend((k) => k.ltv_cents, 'ltv_cents')} days={dates} value={cur?.ltv_cents || 0} loading={loading}
                                note={<>Valor histórico gerado ÷ clientes com compra</>} />
                            </Indicator>

                            {/* Grupo C — retenção e risco. */}
                            <Indicator id="recorrencia">
                            <KpiTickerCard uid="crec" kind="pct" label="Taxa de recorrência" help={HELP.recurrence} icon={<Repeat size={11} strokeWidth={2} />} iconBg="secondary.bg" iconFg="secondary.main"
                                data={trend((k) => k.recurrence_rate, 'recurrence_rate', 100)} days={dates} value={Math.round((cur?.recurrence_rate || 0) * 100)} loading={loading}
                                note={<><b>{fmtInt(cur?.recurring)}</b> de <b>{fmtInt(buyers)}</b> clientes compraram 2+ vezes</>} />
                            </Indicator>
                            <Indicator id="em-risco">
                            <KpiTickerCard inverse hideSpark uid="crisk" kind="units" label="Em risco de inatividade" help={HELP.atRisk} icon={<AlertTriangle size={11} strokeWidth={2} />} iconBg="warning.bg" iconFg="warning.main"
                                data={trend((k) => k.at_risk, null)} value={cur?.at_risk || 0} loading={loading}
                                footer={cur && (
                                    <>
                                        <SegFooter
                                            parts={[{ key: 'risk', value: baseOf(cur.at_risk), color: theme.palette.warning.main }]}
                                            legend={[{ key: 'risk', color: theme.palette.warning.main, text: <><b>{fmtPct1(baseOf(cur.at_risk))}</b> da base · 30–60 dias sem comprar</> }]}
                                        />
                                        <Typography sx={{ fontSize: '10.5px', color: 'text.disabled', mt: '6px' }}>Hora de agir antes de virarem inativos (60 dias)</Typography>
                                    </>
                                )} />
                            </Indicator>
                            <Indicator id="reativados">
                            <KpiTickerCard uid="crv" kind="units" label="Clientes reativados" help={HELP.reactivated} icon={<RotateCw size={11} strokeWidth={2} />} iconBg="success.bg" iconFg="success.main"
                                data={trend((k) => k.reactivated, 'reactivated')} days={dates} value={cur?.reactivated || 0} loading={loading}
                                note={<>Inativos que voltaram a comprar no período</>} />
                            </Indicator>
                            <Indicator id="concentracao">
                            <KpiTickerCard inverse hideSpark uid="cconc" kind="pct" label="Concentração (top 10)" help={HELP.concentration} icon={<PieChart size={11} strokeWidth={2} />} iconBg="error.bg" iconFg="error.main"
                                data={trend((k) => k.concentration_top10, null, 100)} value={Math.round((cur?.concentration_top10 || 0) * 100)} loading={loading}
                                footer={cur && (
                                    <>
                                        <SegFooter
                                            parts={[{ key: 'top', value: cur.concentration_top10, color: theme.palette.primary.main }]}
                                            legend={[{ key: 'top', color: theme.palette.primary.main, text: <><b>{fmtPct1(cur.concentration_top10)}</b> da receita nos 10 maiores</> }]}
                                        />
                                        <Typography sx={{ fontSize: '10.5px', color: 'text.disabled', mt: '6px' }}>Dependência dos maiores clientes (curva ABC)</Typography>
                                    </>
                                )} />
                            </Indicator>

                            {/* Crescimento da base + Novos x Inativados x Reativados. */}
                            <Indicator id="grafico-crescimento">
                            <Card>
                                <CardContent sx={{ p: 2.25 }}>
                                    <CardHeadRow icon={<Users size={18} strokeWidth={2} />} iconBg="info.bg" iconFg="primary.main" title="Crescimento da base de clientes" help={HELP.growthChart}
                                        subtitle={`Total cadastrado por ${unitLabel} · ${startDate.format('DD/MM/YYYY')} a ${endDate.format('DD/MM/YYYY')}`} loading={loading} />
                                    <Box sx={{ height: 230, width: '100%' }}>
                                        {loading ? <Skeleton variant="rounded" width="100%" height={210} /> : hasGrowth ? (
                                            <LineChart
                                                height={210}
                                                margin={{ top: 12, right: 12, bottom: 24, left: 8 }}
                                                hideLegend
                                                grid={{ horizontal: true }}
                                                xAxis={[{ scaleType: 'point', data: growthPoints.map((p) => pointLabel(p)), disableLine: true, disableTicks: true, tickLabelStyle: { fontSize: 10.5, fontWeight: 600, fill: theme.palette.text.disabled } }]}
                                                yAxis={[{ disableLine: true, disableTicks: true, min: Math.max(0, Math.min(...growthPoints.map((p) => p.base)) - 5) }]}
                                                series={[{ data: growthPoints.map((p) => p.base), label: 'Clientes cadastrados', color: theme.palette.primary.main, showMark: growthPoints.length <= 16, valueFormatter: (v) => `${fmtInt(v)} cliente(s)` }]}
                                                sx={{ '& .MuiChartsGrid-line': { stroke: theme.palette.divider }, '& .MuiMarkElement-root': { fill: theme.palette.background.paper, strokeWidth: 2 } }}
                                            />
                                        ) : (
                                            <EmptyMiniState icon={<Users size={22} strokeWidth={2} />} title="Nenhum dado disponível" subtitle="Não há clientes cadastrados no período" />
                                        )}
                                    </Box>
                                    {!loading && hasGrowth && (
                                        <Box sx={{ display: 'flex', gap: 2, mt: 1.5, flexWrap: 'wrap' }}>
                                            <ChartLegendItem color={theme.palette.primary.main} label="Clientes cadastrados (acumulado)" />
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                            </Indicator>
                            <Indicator id="grafico-fluxo">
                            <Card>
                                <CardContent sx={{ p: 2.25 }}>
                                    <CardHeadRow icon={<UserMinus size={18} strokeWidth={2} />} iconBg="secondary.bg" iconFg="secondary.main" title="Novos x Inativados x Reativados" help={HELP.flowChart}
                                        subtitle={`Por ${unitLabel} · inativado = completou 60 dias sem comprar`} loading={loading} />
                                    <Box sx={{ height: 230, width: '100%' }}>
                                        {loading ? <Skeleton variant="rounded" width="100%" height={210} /> : hasFlow ? (
                                            <BarChart
                                                height={210}
                                                margin={{ top: 12, right: 8, bottom: 24, left: 8 }}
                                                borderRadius={4}
                                                categoryGapRatio={0.35}
                                                hideLegend
                                                xAxis={[{ scaleType: 'band', data: growthPoints.map((p) => pointLabel(p)), disableLine: true, disableTicks: true, tickLabelStyle: { fontSize: 10, fontWeight: 600, fill: theme.palette.text.disabled } }]}
                                                yAxis={[{ disableLine: true, disableTicks: true }]}
                                                series={[
                                                    { data: growthPoints.map((p) => p.new), label: 'Novos', color: theme.palette.primary.main, valueFormatter: (v) => `${fmtInt(v)} novo(s)` },
                                                    { data: growthPoints.map((p) => p.inactivated), label: 'Inativados', color: theme.palette.secondary.main, valueFormatter: (v) => `${fmtInt(v)} inativado(s)` },
                                                    { data: growthPoints.map((p) => p.reactivated), label: 'Reativados', color: theme.palette.success.main, valueFormatter: (v) => `${fmtInt(v)} reativado(s)` },
                                                ]}
                                                sx={{ '& .MuiChartsAxis-directionY': { display: 'none' }, '& .MuiChartsGrid-line': { stroke: theme.palette.divider } }}
                                            />
                                        ) : (
                                            <EmptyMiniState icon={<BarChart3 size={22} strokeWidth={2} />} title="Nenhum dado disponível" subtitle="Sem novos, inativados ou reativados no período" />
                                        )}
                                    </Box>
                                    {!loading && hasFlow && (
                                        <Box sx={{ display: 'flex', gap: 2, mt: 1.5, flexWrap: 'wrap' }}>
                                            <ChartLegendItem color={theme.palette.primary.main} label="Novos" />
                                            <ChartLegendItem color={theme.palette.secondary.main} label="Inativados" />
                                            <ChartLegendItem color={theme.palette.success.main} label="Reativados" />
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                            </Indicator>

                            {/* Faixas de gasto + Top 5 valor gasto + Top 5 frequência. */}
                            <Indicator id="faixas-gasto">
                            <Card>
                                <CardContent sx={{ p: 2.25 }}>
                                    <CardHeadRow icon={<BarChart3 size={18} strokeWidth={2} />} iconBg="info.bg" iconFg="primary.main" title="Clientes por faixa de gasto" help={HELP.spendBands} subtitle="Clientes com compra no período" loading={loading}
                                        right={loading ? <Skeleton variant="text" width={48} height={26} /> : (
                                            <>
                                                <Typography sx={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}><AnimatedNumber value={buyers} /></Typography>
                                                <Typography sx={{ fontSize: '10.5px', fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '.04em' }}>clientes</Typography>
                                            </>
                                        )} />
                                    {loading ? [1, 2, 3, 4].map((i) => <BarRow key={i} loading lead={<Skeleton variant="circular" width={10} height={10} />} />) : buyers > 0 ? bands.map((b, i) => (
                                        <BarRow key={b.from_cents}
                                            lead={<Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: bandColors[i], flexShrink: 0 }} />}
                                            label={bandLabel(b)} barWidth={(b.customers / bandMax) * 100} barColor={bandColors[i]}
                                            value={fmtInt(b.customers)} sub={fmtPct1(pctOf(b.customers, buyers))} />
                                    )) : <EmptyMiniState icon={<Users size={22} strokeWidth={2} />} title="Sem compras no período" subtitle="Nenhum cliente comprou no período selecionado" />}
                                </CardContent>
                            </Card>
                            </Indicator>
                            <Indicator id="maior-valor">
                            <Card>
                                <CardContent sx={{ p: 2.25 }}>
                                    <CardHeadRow icon={<DollarSign size={18} strokeWidth={2} />} iconBg="success.bg" iconFg="success.main" title="Maior valor gasto" help={HELP.topValue} subtitle="Top 5 · vendas + OS no período" loading={loading} right={<ReportLink />} />
                                    {loading ? [1, 2, 3, 4, 5].map((i) => <BarRow key={i} loading lead={<Skeleton variant="rounded" width={24} height={24} />} />) : topValue.length ? topValue.map((c, i) => (
                                        <BarRow key={c.customer_id} lead={<RankNum n={i + 1} />} label={c.name} badge={<TypePill type={c.type} />}
                                            barWidth={topValue[0].value_cents > 0 ? (c.value_cents / topValue[0].value_cents) * 100 : 0} barColor={gradient}
                                            value={fmtBrl0(c.value_cents)} sub={`${fmtPct1(c.share_rate)} da receita`} />
                                    )) : <EmptyMiniState icon={<DollarSign size={22} strokeWidth={2} />} title="Sem compras no período" subtitle="Ajuste o período acima" />}
                                </CardContent>
                            </Card>
                            </Indicator>
                            <Indicator id="maior-frequencia">
                            <Card>
                                <CardContent sx={{ p: 2.25 }}>
                                    <CardHeadRow icon={<Repeat size={18} strokeWidth={2} />} iconBg="secondary.bg" iconFg="secondary.main" title="Maior frequência" help={HELP.topFrequency} subtitle="Top 5 · nº de vendas + OS" loading={loading} right={<ReportLink />} />
                                    {loading ? [1, 2, 3, 4, 5].map((i) => <BarRow key={i} loading lead={<Skeleton variant="rounded" width={24} height={24} />} />) : topFreq.length ? topFreq.map((c, i) => (
                                        <BarRow key={c.customer_id} lead={<RankNum n={i + 1} />} label={c.name} badge={<TypePill type={c.type} />}
                                            barWidth={topFreq[0].interactions > 0 ? (c.interactions / topFreq[0].interactions) * 100 : 0} barColor={gradient}
                                            value={`${fmtInt(c.interactions)} ${c.interactions === 1 ? 'compra' : 'compras'}`} sub={`${fmtBrl0(c.value_cents)} gerados`} />
                                    )) : <EmptyMiniState icon={<Repeat size={22} strokeWidth={2} />} title="Sem compras no período" subtitle="Ajuste o período acima" />}
                                </CardContent>
                            </Card>
                            </Indicator>

                            {/* Aniversariantes + Clientes inativos. */}
                            <Indicator id="lista-aniversariantes">
                            <Card>
                                <CardContent sx={{ p: 2.25 }}>
                                    <CardHeadRow icon={<Gift size={18} strokeWidth={2} />} iconBg="warning.bg" iconFg="warning.main" title="Aniversariantes do período" help={HELP.birthdayList}
                                        subtitle={bd ? `${fmtInt(bd.total)} ${bd.total === 1 ? 'aniversariante' : 'aniversariantes'} entre ${isoToDm(data.start_date)} e ${isoToDm(data.end_date)} · oportunidade de contato` : ''}
                                        loading={loading} right={<ReportLink />} />
                                    {loading ? [1, 2, 3].map((i) => <ListRow key={i} avatar={<Skeleton variant="rounded" width={34} height={34} />} title={<Skeleton width={120} />} sub="" end={null} />) : bd?.items?.length ? bd.items.map((p) => (
                                        <ListRow key={`${p.customer_id}-${p.date}`}
                                            avatar={<Avatar name={p.name} bg={theme.palette.warning.bg} fg={theme.palette.warning.main} />}
                                            title={p.name}
                                            sub={<>{p.age} anos{p.phone ? ` · ${p.phone}` : ' · sem telefone'}{p.inactive && <Pill tone="error">inativo</Pill>}</>}
                                            end={<><b style={{ fontSize: 12.5 }}>{isoToDm(p.date)}</b>{p.today && <Pill tone="success">hoje</Pill>}</>} />
                                    )) : <EmptyMiniState icon={<Gift size={22} strokeWidth={2} />} title="Nenhum aniversariante" subtitle="Sem aniversários no período selecionado" />}
                                    {!loading && bd && bd.total > 0 && (
                                        <Typography sx={{ fontSize: '11.5px', color: 'text.disabled', mt: 1.5, pt: 1.25, borderTop: '1px dashed', borderColor: 'divider', '& b': { color: 'text.secondary' } }}>
                                            <b>{fmtInt(bd.also_inactive)}</b> {bd.also_inactive === 1 ? 'aniversariante também está inativo' : 'aniversariantes também estão inativos'} · ótima oportunidade de reengajamento
                                            {bd.total > bd.items.length ? ` · mostrando ${bd.items.length} de ${fmtInt(bd.total)}` : ''}
                                        </Typography>
                                    )}
                                </CardContent>
                            </Card>
                            </Indicator>
                            <Indicator id="lista-inativos">
                            <Card>
                                <CardContent sx={{ p: 2.25 }}>
                                    <CardHeadRow icon={<UserMinus size={18} strokeWidth={2} />} iconBg="error.bg" iconFg="error.main" title="Clientes inativos" help={HELP.inactiveList}
                                        subtitle={inact ? `${fmtInt(inact.total)} clientes sem venda finalizada ou OS paga há mais de 60 dias · maiores valores históricos primeiro` : ''}
                                        loading={loading} right={<ReportLink />} />
                                    {loading ? [1, 2, 3].map((i) => <ListRow key={i} avatar={<Skeleton variant="rounded" width={34} height={34} />} title={<Skeleton width={120} />} sub="" end={null} />) : inact?.items?.length ? inact.items.map((c) => (
                                        <ListRow key={c.customer_id}
                                            avatar={<Avatar name={c.name} bg={theme.palette.error.bg} fg={theme.palette.error.main} />}
                                            title={c.name}
                                            sub={<><TypePill type={c.type} /> {c.has_purchases ? `última interação ${isoToBr(c.last_interaction)}` : `nunca comprou · cadastro em ${isoToBr(c.last_interaction)}`}</>}
                                            end={<><Pill tone={c.days_inactive > 90 ? 'error' : 'warning'}>{fmtInt(c.days_inactive)} dias</Pill><span style={{ fontSize: 11 }}>gastou <b>{fmtBrl0(c.lifetime_cents)}</b></span></>} />
                                    )) : <EmptyMiniState icon={<UserCheck size={22} strokeWidth={2} />} title="Nenhum cliente inativo" subtitle="Toda a base teve interação nos últimos 60 dias" />}
                                    {!loading && inact && inact.total > 0 && (
                                        <Typography sx={{ fontSize: '11.5px', color: 'text.disabled', mt: 1.5, pt: 1.25, borderTop: '1px dashed', borderColor: 'divider', '& b': { color: 'text.secondary' } }}>
                                            Mostrando {inact.items.length} de <b>{fmtInt(inact.total)}</b> · <b>{fmtInt(inact.over_90)}</b> há mais de 90 dias
                                        </Typography>
                                    )}
                                </CardContent>
                            </Card>
                            </Indicator>
                        </DashboardBoard>
                    </>
                )}
            </Box>
        </PageScaffold>
    );
}
